import '../scss/style.scss';

document.documentElement.id = 'top';

const pageTop = document.querySelector('.page-top');
if (pageTop) {
  pageTop.addEventListener('click', (event) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

function visibleCards(track) {
  return Array.from(track.children).filter((el) => !el.hidden);
}

function initHomeCarousel(root) {
  const track = root.querySelector('[data-carousel-track]');
  const nav = root.querySelector('[data-carousel-nav]');
  const dotsWrap = root.querySelector('[data-carousel-dots]');
  const prevBtn = root.querySelector('[data-carousel-prev]');
  const nextBtn = root.querySelector('[data-carousel-next]');
  const viewport = track?.parentElement;
  if (!track || !nav || !dotsWrap || !viewport) return null;

  const mqSp = window.matchMedia('(max-width: 900px)');
  const mqReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const AUTOPLAY_MS = 5000;
  let index = 0;
  let bound = false;
  let inView = true;
  let pointerActive = false;
  let autoplayTimer = 0;

  const syncSlideWidth = () => {
    if (!mqSp.matches) {
      viewport.style.removeProperty('--latest-slide-w');
      return;
    }
    viewport.style.setProperty('--latest-slide-w', `${viewport.clientWidth}px`);
  };

  const getStep = (cards) => {
    if (mqSp.matches) {
      return viewport.clientWidth;
    }
    if (!cards.length) return 0;
    const cardWidth = cards[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(track).gap || '0') || 0;
    return cardWidth + gap;
  };

  const getMaxIndex = (cards) => Math.max(0, cards.length - 1);

  const getMaxTranslate = () => Math.max(0, track.scrollWidth - viewport.clientWidth);

  const clearTransform = () => {
    track.style.transition = 'none';
    track.style.transform = '';
  };

  const stopAutoplay = () => {
    if (!autoplayTimer) return;
    window.clearTimeout(autoplayTimer);
    autoplayTimer = 0;
  };

  const canAutoplay = () =>
    mqSp.matches &&
    !mqReduceMotion.matches &&
    inView &&
    !pointerActive &&
    !document.hidden &&
    visibleCards(track).length > 1;

  const scheduleAutoplay = ({ restart = false } = {}) => {
    if (!canAutoplay()) {
      stopAutoplay();
      return;
    }
    if (autoplayTimer && !restart) return;
    stopAutoplay();
    autoplayTimer = window.setTimeout(() => {
      autoplayTimer = 0;
      if (!canAutoplay()) return;
      const cards = visibleCards(track);
      const maxIndex = getMaxIndex(cards);
      goTo(index >= maxIndex ? 0 : index + 1);
      scheduleAutoplay();
    }, AUTOPLAY_MS);
  };

  const renderDots = (cards) => {
    dotsWrap.innerHTML = '';
    cards.forEach((_, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'home-carousel-nav__dot' + (i === index ? ' is-active' : '');
      button.setAttribute('aria-label', `${i + 1}枚目`);
      button.addEventListener('click', () => {
        goTo(i);
        scheduleAutoplay({ restart: true });
      });
      dotsWrap.appendChild(button);
    });
  };

  const syncIndexFromScroll = () => {
    const cards = visibleCards(track);
    const step = getStep(cards);
    if (step <= 0) return;
    index = Math.min(getMaxIndex(cards), Math.round(viewport.scrollLeft / step));
    dotsWrap.querySelectorAll('.home-carousel-nav__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  };

  const goTo = (nextIndex, animate = true) => {
    const cards = visibleCards(track);
    if (!cards.length) {
      clearTransform();
      viewport.scrollLeft = 0;
      return;
    }

    const maxIndex = getMaxIndex(cards);
    index = Math.min(Math.max(nextIndex, 0), maxIndex);

    if (mqSp.matches) {
      clearTransform();
      const left = index * getStep(cards);
      if (animate) {
        viewport.scrollTo({ left, behavior: 'smooth' });
      } else {
        viewport.scrollLeft = left;
      }
      dotsWrap.querySelectorAll('.home-carousel-nav__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
      return;
    }

    const translate = Math.min(index * getStep(cards), getMaxTranslate());
    track.style.transition = animate ? '' : 'none';
    track.style.transform = `translateX(-${translate}px)`;
    dotsWrap.querySelectorAll('.home-carousel-nav__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  };

  const measure = () => {
    const cards = visibleCards(track);
    if (index > getMaxIndex(cards)) index = getMaxIndex(cards);
    syncSlideWidth();
    renderDots(cards);

    if (cards.length <= 1) {
      clearTransform();
      viewport.scrollLeft = 0;
      nav.hidden = true;
      nav.dataset.spScroll = '';
      stopAutoplay();
      return;
    }

    if (mqSp.matches) {
      clearTransform();
      nav.hidden = false;
      nav.dataset.spScroll = 'true';
      goTo(index, false);
      scheduleAutoplay();
      return;
    }

    nav.dataset.spScroll = '';
    goTo(index, false);
    nav.hidden = getMaxTranslate() <= 0;
    stopAutoplay();
  };

  const refresh = () => {
    index = 0;
    measure();
  };

  if (!bound) {
    prevBtn?.addEventListener('click', () => {
      goTo(index - 1);
      scheduleAutoplay({ restart: true });
    });
    nextBtn?.addEventListener('click', () => {
      goTo(index + 1);
      scheduleAutoplay({ restart: true });
    });
    viewport.addEventListener(
      'scroll',
      () => {
        if (!mqSp.matches) return;
        syncIndexFromScroll();
      },
      { passive: true },
    );
    viewport.addEventListener('pointerdown', () => {
      pointerActive = true;
      stopAutoplay();
    });
    const onPointerEnd = () => {
      if (!pointerActive) return;
      pointerActive = false;
      scheduleAutoplay({ restart: true });
    };
    window.addEventListener('pointerup', onPointerEnd);
    window.addEventListener('pointercancel', onPointerEnd);
    document.addEventListener('visibilitychange', () => {
      scheduleAutoplay();
    });
    mqReduceMotion.addEventListener('change', () => {
      scheduleAutoplay();
    });
    const observer = new IntersectionObserver(
      ([entry]) => {
        const next = Boolean(entry?.isIntersecting);
        if (next === inView) return;
        inView = next;
        scheduleAutoplay();
      },
      { threshold: 0.35 },
    );
    observer.observe(root);
    window.addEventListener('resize', measure);
    mqSp.addEventListener('change', measure);
    bound = true;
  }

  measure();
  return { refresh };
}

const homeCarousels = Array.from(document.querySelectorAll('[data-home-carousel]'))
  .map((root) => initHomeCarousel(root))
  .filter(Boolean);

function readCategoryParam() {
  return new URLSearchParams(window.location.search).get('category') || '';
}

function syncChannelNav(channel) {
  const nav = document.querySelector('[data-home-channel-nav]');
  if (!nav) return;

  nav.querySelectorAll('.home-nav__link').forEach((link) => {
    const linkChannel = link.getAttribute('data-channel') ?? '';
    link.classList.toggle('is-active', linkChannel === channel);
  });
}

function articlesListHref(channel) {
  const params = new URLSearchParams({ section: 'latest' });
  if (channel) params.set('category', channel);
  return `/articles/?${params.toString()}`;
}

function syncMoreButtons(channel) {
  document.querySelectorAll('[data-articles-more-channel]').forEach((btn) => {
    btn.setAttribute('href', articlesListHref(channel));
  });
}

function syncHomeMidBlocks() {
  const section = document.querySelector('[data-home-mid-section]');
  if (!section) return;

  let visibleBlockCount = 0;
  section.querySelectorAll('[data-home-mid-block]').forEach((block) => {
    const grid = block.querySelector('[data-home-mid-grid]');
    if (!grid) return;

    const visibleCards = Array.from(grid.querySelectorAll('.article-card'))
      .filter((card) => !card.hidden);
    const hasVisibleCards = visibleCards.length > 0;

    block.hidden = !hasVisibleCards;
    const moreBtn = block.querySelector('[data-home-mid-more]');
    if (moreBtn) moreBtn.hidden = !hasVisibleCards;

    if (hasVisibleCards) visibleBlockCount += 1;
  });

  section.hidden = visibleBlockCount === 0;
}

function syncHomePickupBlocks() {
  const section = document.querySelector('[data-home-pickup-section]');
  if (!section) return;

  let visibleBlockCount = 0;
  section.querySelectorAll('[data-home-pickup-block]').forEach((block) => {
    const grid = block.querySelector('[data-home-pickup-grid]');
    if (!grid) return;

    const visibleCards = Array.from(grid.querySelectorAll('.article-card'))
      .filter((card) => !card.hidden);
    const hasVisibleCards = visibleCards.length > 0;

    block.hidden = !hasVisibleCards;
    const moreBtn = block.querySelector('[data-home-pickup-more]');
    if (moreBtn) moreBtn.hidden = !hasVisibleCards;

    if (hasVisibleCards) visibleBlockCount += 1;
  });

  section.hidden = visibleBlockCount === 0;
}

function applyChannelFilter(channel) {
  document.querySelectorAll('.article-card[data-channel]').forEach((card) => {
    const cardChannel = card.getAttribute('data-channel') || '';
    card.hidden = Boolean(channel) && cardChannel !== channel;
  });

  // data-channel が無いカードは「すべて」以外では隠す
  document.querySelectorAll('.article-card:not([data-channel])').forEach((card) => {
    card.hidden = Boolean(channel);
  });

  homeCarousels.forEach((carousel) => carousel.refresh());
  syncHomePickupBlocks();
  syncHomeMidBlocks();
  syncChannelNav(channel);
  syncMoreButtons(channel);
}

function writeCategoryParam(channel) {
  const url = new URL(window.location.href);
  if (channel) {
    url.searchParams.set('category', channel);
  } else {
    url.searchParams.delete('category');
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function initHomeChannelFilter() {
  const nav = document.querySelector('[data-home-channel-nav]');
  if (!nav) return;

  nav.addEventListener('click', (event) => {
    const link = event.target.closest('.home-nav__link');
    if (!link || !nav.contains(link)) return;

    event.preventDefault();
    const channel = link.getAttribute('data-channel') ?? '';
    writeCategoryParam(channel);
    applyChannelFilter(channel);
  });

  applyChannelFilter(readCategoryParam());
}

initHomeChannelFilter();

function normalizeTagLabel(value) {
  return (value || '').trim().replace(/^#+/, '');
}

function readTagParams() {
  return new URLSearchParams(window.location.search)
    .getAll('tag')
    .map(normalizeTagLabel)
    .filter(Boolean);
}

function cardCategoryLabels(card) {
  return (card.getAttribute('data-categories') || '')
    .split(',')
    .map((label) => label.trim())
    .filter(Boolean);
}

function cardMatchesTags(card, tags) {
  if (!tags.length) return true;
  const labels = cardCategoryLabels(card);
  return tags.some((tag) => labels.includes(tag));
}

function cardMatchesChannel(card, channel) {
  if (!channel) return true;
  return (card.getAttribute('data-channel') || '') === channel;
}

function cardMatchesSection(card, section) {
  if (section !== 'pickup') return true;
  return card.hasAttribute('data-featured');
}

function initArticlesIndexFilter() {
  const grid = document.querySelector('.articles-index [data-articles-grid]');
  if (!grid) return;

  const emptyEl = document.querySelector('[data-articles-empty-filtered]');
  const filterHead = document.querySelector('[data-articles-filter-head]');
  const filterTitle = document.querySelector('[data-articles-filter-title]');
  const sectionRoot = document.querySelector('.articles-index');
  const params = new URLSearchParams(window.location.search);
  const tags = readTagParams();
  const channel = readCategoryParam();
  const label = params.get('label') || '';
  const section = params.get('section') || '';
  let visibleCount = 0;

  document.querySelectorAll('[data-articles-label]').forEach((el) => {
    el.hidden = true;
  });

  if (section === 'latest') {
    const latestLabel = document.querySelector('[data-articles-label="latest"]');
    if (latestLabel) latestLabel.hidden = false;
    if (sectionRoot) sectionRoot.setAttribute('aria-labelledby', 'articles-index-heading-latest');
  } else if (section === 'pickup') {
    const pickupLabel = document.querySelector('[data-articles-label="pickup"]');
    if (pickupLabel) pickupLabel.hidden = false;
    if (sectionRoot) sectionRoot.setAttribute('aria-labelledby', 'articles-index-heading-pickup');
  } else {
    const defaultLabel = document.querySelector('[data-articles-label="default"]');
    if (defaultLabel) defaultLabel.hidden = false;
    if (sectionRoot) sectionRoot.setAttribute('aria-labelledby', 'articles-index-heading');
  }

  if (label && filterHead && filterTitle && !section) {
    filterTitle.textContent = label;
    filterHead.hidden = false;
  } else if (filterHead) {
    filterHead.hidden = true;
  }

  grid.querySelectorAll('.article-card').forEach((card) => {
    const visible = cardMatchesTags(card, tags)
      && cardMatchesChannel(card, channel)
      && cardMatchesSection(card, section);
    card.hidden = !visible;
    if (visible) visibleCount += 1;
  });

  if (emptyEl) {
    emptyEl.hidden = visibleCount > 0;
  }
}

initArticlesIndexFilter();

/** Load Instagram embed.js once and process blockquotes on the page. */
function initInstagramEmbeds() {
  const embeds = document.querySelectorAll('blockquote.instagram-media');
  if (!embeds.length) return;

  const runProcess = () => {
    if (window.instgrm?.Embeds?.process) {
      window.instgrm.Embeds.process();
    }
  };

  if (window.instgrm?.Embeds?.process) {
    runProcess();
    return;
  }

  const existing = document.querySelector('script[data-instagram-embed]');
  if (existing) {
    existing.addEventListener('load', runProcess);
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://www.instagram.com/embed.js';
  script.async = true;
  script.dataset.instagramEmbed = '1';
  script.addEventListener('load', runProcess);
  document.body.appendChild(script);
}

initInstagramEmbeds();

/** Share buttons (LINE / Facebook / X) */
function getPageShareUrl() {
  const meta = document.querySelector('meta[property="og:url"]')?.content
    || document.querySelector('link[rel="canonical"]')?.href
    || window.location.href;
  return meta;
}

function getShareText(title, description) {
  const desc = (description || '').replace(/\s+/g, ' ').trim();
  const base = title || document.title;
  if (desc && desc !== base) return `${base} — ${desc}`;
  return base;
}

function initShareButtons() {
  const root = document.querySelector('.article__share');
  if (!root) return;

  const title = document.querySelector('h1.article__title')?.textContent || '';
  const description = document.querySelector('meta[name="description"]')?.content || '';
  const pageUrl = getPageShareUrl();
  const shareText = getShareText(title, description);

  root.querySelectorAll('[data-share]').forEach((link) => {
    const network = link.dataset.share;
    let href = '#';
    if (network === 'facebook') {
      href = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(pageUrl)}`;
    } else if (network === 'x') {
      href = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`;
    } else if (network === 'line') {
      const lineText = `${shareText} ${pageUrl}`;
      href = `https://line.me/R/share?text=${encodeURIComponent(lineText)}`;
    }
    if (href !== '#') {
      link.href = href;
    }
  });
}

initShareButtons();
